import { Link } from '@inertiajs/react'
import { Check, Disc3, Globe, Megaphone, Music, ShieldCheck, Wallet } from 'lucide-react'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import LogoRail from '@/components/marketing/LogoRail'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface PlanCard {
  name: string
  eur_yearly: string
  eur_monthly: string
  usd_yearly: string
  royalty: string
  badge?: string
  description: string
  bullets: string[]
}

interface GermanyPageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    tagline_german?: string
    title: string
    body: string
    bg_image?: string
    primary_cta: { label: string; href: string }
    secondary_cta: { label: string; href: string }
    metrics: { value: string; label: string }[]
  }
  languages: string[]
  features: { title: string; body: string; icon: string }[]
  platform_logos: { name: string; src: string }[]
  artist_plans: PlanCard[]
  label_plans: PlanCard[]
  included_note: string
  payment_note: string
  trust_signals: string[]
  faq_items: { question: string; answer: string }[]
  cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
}

const iconMap = {
  wallet: Wallet,
  languages: Globe,
  music: Music,
  disc: Disc3,
  shield: ShieldCheck,
  megaphone: Megaphone,
} as const

function GermanyPricingCard({ plan }: { plan: PlanCard }) {
  const isHighlighted = !!plan.badge

  return (
    <div
      className={`group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border p-7 transition-all duration-200 hover:-translate-y-1 ${
        isHighlighted
          ? 'border-[#ff6a1a]/30 bg-gradient-to-br from-[#ff6a1a]/10 to-[#ff6a1a]/[0.02] hover:border-[#ff6a1a]/50 hover:shadow-[0_20px_50px_rgba(255,106,26,0.12)]'
          : 'border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] hover:border-white/[0.16] hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]'
      }`}
    >
      {isHighlighted && (
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#ff6a1a]/[0.08] blur-3xl" />
      )}

      <div className="relative flex items-center justify-between">
        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
          {plan.name}
        </h3>
        {plan.badge ? (
          <span className="rounded-full bg-[#ff6a1a]/20 px-2.5 py-0.5 text-[0.65rem] font-bold text-[#ff6a1a]">
            {plan.badge}
          </span>
        ) : (
          <span className="rounded-full bg-white/[0.08] px-2.5 py-0.5 text-xs font-bold text-white/60">
            {plan.royalty}
          </span>
        )}
      </div>

      <div className="relative mt-4">
        <p className="text-3xl font-bold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
          {plan.eur_monthly}
        </p>
        <p className="mt-0.5 text-sm text-white/48">oder {plan.eur_yearly}</p>
      </div>

      <div className="relative my-5 h-px bg-white/[0.06]" />

      <p className="relative text-sm leading-relaxed text-white/60">{plan.description}</p>

      <ul className="relative mt-5 flex-1 space-y-2.5">
        {plan.bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-2.5 text-sm text-white/72">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isHighlighted ? 'text-[#ff6a1a]' : 'text-white/40'}`} />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>

      <a
        href="/apply"
        className={`relative mt-6 block rounded-xl py-3 text-center text-sm font-bold transition-all duration-200 ${
          isHighlighted
            ? 'bg-[#ff6a1a] text-[#1a0800] hover:bg-[#ff8c4a] hover:shadow-[0_8px_24px_rgba(255,106,26,0.3)]'
            : 'border border-white/[0.12] bg-white/[0.04] text-white hover:border-white/[0.24] hover:bg-white/[0.08]'
        }`}
      >
        Jetzt bewerben
      </a>
    </div>
  )
}

export default function Germany({
  hero,
  languages,
  features,
  platform_logos,
  artist_plans,
  label_plans,
  included_note,
  payment_note,
  trust_signals,
  faq_items,
  cta,
}: GermanyPageProps) {
  return (
    <div lang="de">
      {/* Hero — same v2 layered treatment as the homepage. */}
      <section className="marketing-hero-section hero-v2 relative overflow-hidden" style={{ minHeight: '78vh' }}>
        {hero.bg_image && (
          <div className="hero-bg" style={{ backgroundImage: `url(${hero.bg_image})` }} />
        )}
        <div className="hero-fade" aria-hidden="true" />
        <div className="hero-spot-warm" aria-hidden="true" />
        <div className="hero-spot-cool" aria-hidden="true" />
        <div className="hero-bottom-fade" aria-hidden="true" />

        <div className="hero-content container relative z-10 mx-auto px-6 pb-8 pt-10 lg:pb-10 lg:pt-12">
          <div className="hero-text">
            <span className="hero-eyebrow">{hero.eyebrow}</span>

            {/* German-language h1 (page lang="de") reusing the title's own
                phrasing; visual slogan demoted to <p> — pixel-identical. */}
            <h1 className="sr-only">Musikvertrieb für Künstler & Labels</h1>
            <p className="hero-headline">
              <span>Drop it.</span>
              <span className="grad-text">Own it.</span>
              <span>Get paid.</span>
            </p>

            {hero.tagline_german && (
              <p
                className="grad-text -mt-2 text-2xl font-medium lg:text-3xl"
                style={{ fontFamily: 'var(--wavbee-font-display)' }}
              >
                {hero.tagline_german}
              </p>
            )}

            <p className="hero-body">{hero.body}</p>

            <div className="hero-ctas">
              <a href={hero.primary_cta.href} className="marketing-button-primary">
                {hero.primary_cta.label}
              </a>
              <Link href={hero.secondary_cta.href} className="marketing-button-secondary" prefetch>
                {hero.secondary_cta.label}
              </Link>
            </div>
          </div>

          <div className="hero-stats">
            <div className="hero-stat-grid">
              {hero.metrics.map((metric) => (
                <div key={metric.label} className="hero-stat-card">
                  <span className="hero-stat-value">{metric.value}</span>
                  <span className="hero-stat-label">{metric.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Languages — cream slab */}
      <section id="metadata-languages" className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Metadaten</div>
              <h2 className="marketing-section-title marketing-section-title-dark">20+ europäische Metadaten-Sprachen unterstützt</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#3b3b3b]">
                Titel, Credits und Lyrics können in Deutsch, Englisch, Türkisch, Französisch, Spanisch,
                Italienisch, Polnisch und vielen weiteren europäischen Sprachen geliefert werden.
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="flex flex-wrap gap-3">
              {languages.map((lang) => (
                <span
                  key={lang}
                  className="rounded-full border border-[#151515]/12 bg-white/70 px-4 py-2 text-sm text-[#202020] backdrop-blur"
                >
                  {lang}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Platform logos — dark */}
      <section id="platforms" className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Plattformen</div>
              <h2 className="marketing-section-title">Spotify, Apple Music, YouTube Music und 100+ weitere</h2>
              <p className="marketing-lead max-w-3xl">
                Erreiche Hörer auf Spotify, Apple Music, YouTube Music, Deezer, Amazon Music, TikTok,
                Tidal, SoundCloud, Instagram, Facebook und mehr — alles aus einem Dashboard.
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="fade">
            <LogoRail items={platform_logos} />
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="mt-8 flex justify-center">
              <Link href="/stores" className="marketing-button-secondary" prefetch>
                Alle Stores ansehen →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Features — cream */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Für Deutschland gebaut</div>
              <h2 className="marketing-section-title marketing-section-title-dark">EUR-Zahlung, GEMA-kompatibel, Content ID und persönlicher Support</h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#3b3b3b]">
                Alles, was du brauchst, um aus Deutschland zu veröffentlichen — Abrechnung in EUR,
                Auszahlung aufs deutsche Konto, GEMA-kompatibler Master-Vertrieb und Support während
                deutscher Geschäftszeiten.
              </p>
            </div>
          </ScrollReveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, i) => {
              const Icon = iconMap[feature.icon as keyof typeof iconMap]
              return (
                <ScrollReveal key={feature.title} delay={i * 0.06}>
                  <div className="marketing-light-card h-full">
                    {Icon && (
                      <div className="marketing-icon-badge mb-4">
                        <Icon className="h-5 w-5" />
                      </div>
                    )}
                    <h3 className="text-base font-semibold text-[#151515]">{feature.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#3b3b3b]">{feature.body}</p>
                  </div>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Pricing — merged artist + label, dark, with one warm glow */}
      <section id="pricing" className="marketing-section has-glow">
        <div className="section-glow warm" style={{ width: '38rem', height: '38rem', right: '-10rem', bottom: '-14rem' }} />
        <div className="container mx-auto px-6 space-y-16">
          <div>
            <ScrollReveal>
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">Für Künstler</div>
                <h2 className="marketing-section-title">Tarife rund um deinen Release</h2>
              </div>
            </ScrollReveal>
            <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
              {artist_plans.map((plan, i) => (
                <ScrollReveal key={plan.name} delay={i * 0.08}>
                  <GermanyPricingCard plan={plan} />
                </ScrollReveal>
              ))}
            </div>
          </div>

          <div>
            <ScrollReveal>
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">Für Labels</div>
                <h2 className="marketing-section-title">Verwalte dein gesamtes Roster</h2>
              </div>
            </ScrollReveal>
            <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
              {label_plans.map((plan, i) => (
                <ScrollReveal key={plan.name} delay={i * 0.08}>
                  <GermanyPricingCard plan={plan} />
                </ScrollReveal>
              ))}
            </div>
            <ScrollReveal delay={0.3}>
              <p className="mt-6 text-center text-sm text-white/40">{included_note}</p>
              <p className="mt-3 text-center text-sm text-white/48">{payment_note}</p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Trust signals + FAQ — merged cream zone */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6 space-y-28">
          <div>
            <ScrollReveal>
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">Warum Künstler in Deutschland wavbee wählen</div>
                <h2 className="marketing-section-title marketing-section-title-dark">Erschwinglich. Schnell. Unterstützend.</h2>
              </div>
            </ScrollReveal>
            <div className="mx-auto max-w-3xl">
              <ScrollReveal delay={0.05}>
                <div className="marketing-light-card">
                  <ul className="space-y-3">
                    {trust_signals.map((signal) => (
                      <li key={signal} className="flex items-start gap-3 text-sm text-[#202020]">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#ff5400]" />
                        <span>{signal}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            </div>
          </div>

          <div id="faq">
            <ScrollReveal>
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">Häufig gestellte Fragen</div>
                <h2 className="marketing-section-title marketing-section-title-dark">Was Künstler in Deutschland vor dem Vertrieb fragen</h2>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div className="mx-auto max-w-4xl">
                <FaqAccordion items={faq_items} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA — dark, matches the homepage and India send-off */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow-mono">Loslegen</div>
                <h2 className="marketing-section-title">{cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {cta.actions.map((action, index) => (
                  <a
                    key={action.label}
                    href={action.href}
                    className={index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}
                  >
                    {action.label}
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
